/**
 * SheetBindingForm — client island for the workbook overview page.
 *
 * Renders one card per sheet with:
 *   - Sheet name + row count
 *   - Template <select> showing the sheet's actual binding (nothing is
 *     pre-selected from the sheet's detected product type)
 *   - "Saved ✓" flash on PUT …/binding success
 *   - "Open mapper →" link
 *
 * Also renders the workbook-level export flow, which starts a background job
 * and polls until a CSV download is ready.
 */

import { useState } from "react";
import { Link } from "react-router";
import { setWorkbookSheetBinding } from "@/lib/api/endpoints";
import { WorkbookExportFlow } from "./WorkbookExportFlow";
import type { ParsedSheet, ProductType } from "@/lib/mapper/types";

// ---------------------------------------------------------------------------
// Types mirroring what WorkbookPage passes down.
// We avoid importing Prisma types in the client bundle.
// ---------------------------------------------------------------------------

export type ProfileSummary = {
  id: string;
  name: string;
  productType: string;
  type: string;
  isDefault: boolean;
};

export type BindingSummary = {
  sheetName: string;
  profileId: string | null;
  profile: ProfileSummary | null;
};

export type WorkbookSummary = {
  id: string;
  originalName: string;
  sheets: ParsedSheet[];
  detected: Record<string, { type: ProductType; confidence: number }>;
  createdAt: string; // ISO string
};

type Props = {
  workbook: WorkbookSummary;
  bindings: BindingSummary[];
  profiles: ProfileSummary[];
};


function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function SheetBindingForm({ workbook, bindings, profiles }: Props) {
  const sortedProfiles = [...profiles].sort((a, b) => a.name.localeCompare(b.name));

  // Map sheetName → current profileId (mutable in UI without a full page reload).
  const [selectedProfiles, setSelectedProfiles] = useState<Record<string, string | null>>(() => {
    const init: Record<string, string | null> = {};
    for (const b of bindings) {
      init[b.sheetName] = b.profileId;
    }
    return init;
  });

  // "Saved ✓" flash: key → true while fading
  const [savedFlash, setSavedFlash] = useState<Record<string, boolean>>({});

  // ---------------------------------------------------------------------------
  // Binding save
  // ---------------------------------------------------------------------------
  async function handleProfileChange(sheetName: string, profileId: string) {
    const value = profileId === "" ? null : profileId;
    setSelectedProfiles((prev) => ({ ...prev, [sheetName]: value }));

    try {
      await setWorkbookSheetBinding(workbook.id, sheetName, value);

      // Flash "Saved ✓" for 1.5s
      setSavedFlash((prev) => ({ ...prev, [sheetName]: true }));
      setTimeout(() => {
        setSavedFlash((prev) => ({ ...prev, [sheetName]: false }));
      }, 1500);
    } catch {
      // On error, revert optimistic update
      setSelectedProfiles((prev) => ({
        ...prev,
        [sheetName]: bindings.find((b) => b.sheetName === sheetName)?.profileId ?? null,
      }));
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-5 lg:p-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Link to="/workbooks" className="hover:text-slate-800 dark:hover:text-slate-200 hover:underline">
          Workbooks
        </Link>
        <span>/</span>
        <span className="font-medium text-slate-700 dark:text-slate-300 truncate max-w-xs" title={workbook.originalName}>
          {workbook.originalName}
        </span>
      </nav>

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {workbook.originalName}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {workbook.sheets.length} sheet{workbook.sheets.length === 1 ? "" : "s"}{" "}
            &middot; uploaded {formatDate(workbook.createdAt)}
          </p>
        </div>

        <WorkbookExportFlow workbookId={workbook.id} />
      </div>

      <p className="text-[12.5px] leading-5 text-slate-500 dark:text-slate-400">
        Each sheet becomes Shopify rows through its <strong className="font-medium text-slate-700 dark:text-slate-300">template</strong> (a
        saved column mapping). Change a sheet&apos;s template here, or open the
        mapper to edit its columns. Sheets without a template are skipped on export.
      </p>

      {/* Sheet cards */}
      <div className="space-y-3">
        {workbook.sheets.map((sheet) => {
          const currentProfileId = selectedProfiles[sheet.name] ?? null;

          return (
            <div
              key={sheet.name}
              className="rounded border border-slate-200 dark:border-border bg-white dark:bg-surface shadow-sm"
            >
              {/* Card header row */}
              <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 dark:border-border px-4 py-3">
                <span className="flex-1 min-w-0 truncate text-sm font-semibold text-slate-900 dark:text-slate-100" title={sheet.name}>
                  {sheet.name}
                </span>

                {/* Row count */}
                <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                  {sheet.rowCount.toLocaleString()} row{sheet.rowCount === 1 ? "" : "s"}
                </span>
              </div>

              {/* Card body: profile picker + open mapper */}
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                {/* Profile picker */}
                <label className="flex flex-1 min-w-0 items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                  <span className="shrink-0 font-medium">Template</span>
                  <div className="relative flex-1 min-w-0">
                    <select
                      value={currentProfileId ?? ""}
                      onChange={(e) => handleProfileChange(sheet.name, e.target.value)}
                      className="w-full rounded border border-slate-300 dark:border-border bg-white dark:bg-surface py-1.5 pl-2 pr-7 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
                    >
                      <option value="">No template (skipped on export)</option>
                      {sortedProfiles.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>

                {/* Save flash */}
                <span
                  className={[
                    "text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 transition-opacity duration-300",
                    savedFlash[sheet.name] ? "opacity-100" : "opacity-0",
                  ].join(" ")}
                  aria-live="polite"
                >
                  Saved ✓
                </span>

                {/* Edit sheet — opens the mapper on its editable Source tab */}
                <Link
                  to={`/workbooks/${workbook.id}/sheets/${encodeURIComponent(sheet.name)}?tab=source`}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-surface-2"
                >
                  Edit sheet
                </Link>

                <Link
                  to={`/workbooks/${workbook.id}/sheets/${encodeURIComponent(sheet.name)}`}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-surface-2"
                >
                  Open mapper
                  <svg
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </Link>
              </div>
            </div>
          );
        })}

        {workbook.sheets.length === 0 && (
          <div className="rounded border border-dashed border-slate-300 dark:border-border bg-white dark:bg-surface p-10 text-center">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">No sheets found</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              This workbook appears to have no parseable sheets.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
